import React from 'react';
import { inr, inrCompact } from '../poPayment';

const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

function cellClass(v, max) {
  if (!v) return 'bg-slate-50 border-slate-200/50 text-slate-300';
  const r = v / (max || 1);
  if (r < 0.25) return 'bg-fuchsia-50 border-fuchsia-200/70 text-fuchsia-900 hover:bg-fuchsia-100';
  if (r < 0.5) return 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-950 hover:bg-fuchsia-200';
  if (r < 0.8) return 'bg-fuchsia-500 border-fuchsia-600 text-white hover:bg-fuchsia-600 font-semibold';
  return 'bg-fuchsia-700 border-fuchsia-800 text-white hover:bg-fuchsia-800 font-bold shadow-sm';
}

export default function POHeatmap({ matrix, onDrill }) {
  const [showAll, setShowAll] = React.useState(false);
  const clients = matrix?.clients || [];
  const cats = matrix?.categories || [];
  const cells = React.useMemo(() => {
    const m = {};
    (matrix?.cells || []).forEach((c) => { m[`${c.client}||${c.category}`] = c; });
    return m;
  }, [matrix]);
  const max = Math.max(0, ...(matrix?.cells || []).map((c) => c.total));
  const visible = showAll ? clients : clients.slice(0, 10);

  if (!clients.length) return <div className="soft-card p-8 text-center text-sm text-slate-500">No POs match these filters</div>;

  return (
    <div className="soft-card p-4 sm:p-5" data-testid="po-heatmap">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-[#1a1a2e]">Client × Category</h3>
          <p className="text-xs text-slate-500">Tap any cell to see the POs behind it</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-500">
          <span>Low</span>
          {['bg-fuchsia-50', 'bg-fuchsia-100', 'bg-fuchsia-500', 'bg-fuchsia-700'].map((c) => <span key={c} className={`w-4 h-3 rounded-sm ${c}`} />)}
          <span>High</span>
        </div>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full border-separate" style={{ borderSpacing: 4 }}>
          <thead>
            <tr>
              <th className="text-left text-[11px] font-semibold text-slate-500 px-2 min-w-[160px]">Client</th>
              {cats.map((c) => <th key={c.key} className="text-[11px] font-semibold text-slate-600 px-1 min-w-[92px] whitespace-nowrap">{c.label}</th>)}
              <th className="text-right text-[11px] font-semibold text-slate-600 px-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((cl) => (
              <tr key={cl.key}>
                <td className="text-sm font-medium text-[#1a1a2e] px-2 truncate max-w-[200px]" title={cl.label}>{cl.label}</td>
                {cats.map((cat) => {
                  const cell = cells[`${cl.key}||${cat.key}`];
                  return (
                    <td key={cat.key} className="p-0">
                      <button type="button" disabled={!cell} onClick={() => onDrill({ client: cl.key, category: cat.key, title: `${cl.label} · ${cat.label}` })}
                        className={`w-full rounded-lg border px-2 py-2 text-center transition-[background-color,transform] duration-150 enabled:hover:scale-[1.04] disabled:cursor-default ${cellClass(cell?.total, max)}`}
                        title={cell ? `${inr(cell.total)} · ${cell.count} PO${cell.count > 1 ? 's' : ''}` : 'No POs'}
                        data-testid={`heatmap-cell-${slug(cl.key)}-${slug(cat.key)}`}>
                        <span className="block text-xs tabular-nums">{cell ? inrCompact(cell.total) : '—'}</span>
                        {cell && <span className="block text-[10px] opacity-75">{cell.count} PO{cell.count > 1 ? 's' : ''}</span>}
                      </button>
                    </td>
                  );
                })}
                <td className="text-right text-sm font-semibold tabular-nums text-[#1a1a2e] px-2 whitespace-nowrap">{inrCompact(cl.total)}</td>
              </tr>
            ))}
            <tr>
              <td className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-2 pt-2">Total</td>
              {cats.map((c) => <td key={c.key} className="text-center text-xs font-semibold tabular-nums text-slate-700 pt-2">{inrCompact(c.total)}</td>)}
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {clients.length > 10 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs font-semibold text-fuchsia-700 hover:underline" data-testid="po-heatmap-toggle-all">
          {showAll ? 'Show top 10 clients' : `Show all ${clients.length} clients`}
        </button>
      )}
    </div>
  );
}
